import React, { useEffect, useMemo, useState } from "react"

import { PracticePayerMappingsBulkMode } from "@common-shared/config/practice.config"
import type { IPagination } from "@common-shared/types/general.types"

import { BrowserStorageType } from "@front-shared/config/general.config"
import { NirvanaBreathingLoaderElement } from "@front-shared/elements/nirvanaBreathingLoader.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"
import { BrowserStorageHelper } from "@front-shared/helpers/browserStorage.helper"
import { ConfirmationHelper } from "@front-shared/helpers/confirmation.helper"
import { ToastrHelper } from "@front-shared/helpers/toastr.helper"
import type { ISelectRenderedOption } from "@front-shared/types/objects.types"

import { PAYER_MAPPINGS_CONFIG } from "../../../../config/payerMappings.config"
import { POLICIES_CONFIG } from "../../../../config/policies.config"
import { PayerMappingsCsvHelper } from "../../../../helpers/payerMappingsCsv.helper"
import { RuntimeHelper } from "../../../../helpers/runtime.helper"
import { useAppDispatch } from "../../../../store/appDispatch.hook"
import { useAppSelector } from "../../../../store/selectors/app.selector"
import { payerMappingsSelector } from "../../../../store/selectors/payerMappings.selector"
import { runtimeSelector } from "../../../../store/selectors/runtime.selector"
import { selectedPracticeRoleSelector } from "../../../../store/selectors/selectedPracticeRole.selector"
import { payerMappingsUpsertBulk } from "../../../../store/thunks/payerMappings.thunks"
import type { IReviewPayerMappingsState } from "../../../../types/payerMappings.types"
import { PoliciesListPaginationComponent } from "../../../policies/policiesList/policiesListPagination.component"
import { PayerMappingsBulkUploadPopupComponent } from "../../../popups/payerMappingsBulkUploadPopup.component"
import { PayerMappingsReviewFloatingControlComponent } from "../payerMappingsReviewFloatingControl.component"
import { PayerMappingsTableHeaderComponent } from "./payerMappingsTableHeader.component"
import { PayerMappingsTableRowComponent } from "./payerMappingsTableRow.component"

import closeDarkImg from "@front-shared/assets/images/icons/close-dark.svg"

export const PayerMappingsReviewComponent: React.FunctionComponent<
    IPayerMappingsReviewComponentProps
> = props => {
    const dispatch = useAppDispatch()

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const runtimeState = useAppSelector(runtimeSelector)
    const payerMappingsState = useAppSelector(payerMappingsSelector)

    const isSubmitting = useMemo<boolean>(() => {
        return RuntimeHelper.isPayerMappingsUpsertBulkLoading()
    }, [runtimeState.isLoading])

    const [isReuploadPopupActive, setIsReuploadPopupActive] =
        useState<boolean>(false)

    const [reviewStart, setReviewStart] = useState(0)

    const [selectedPaginationOption, setSelectedPaginationOption] =
        useState<ISelectRenderedOption>(
            BrowserStorageHelper.get<ISelectRenderedOption>(
                PAYER_MAPPINGS_CONFIG.reviewListPerPageStorageKey,
                undefined,
                BrowserStorageType.sessionStorage,
            ) ?? POLICIES_CONFIG.availablePaginationOptions[0],
        )

    const reviewCount = Number(selectedPaginationOption.value)
    const reviewTotal = props.state.items.length

    // Start of the last page, not the last item offset: clamping to
    // total - count lands between pages and desyncs the page indicator
    const maxReviewStart = useMemo<number>(() => {
        return (
            Math.max(0, Math.ceil(reviewTotal / reviewCount) - 1) * reviewCount
        )
    }, [reviewTotal, reviewCount])

    const reviewClampedStart = useMemo<number>(() => {
        return Math.min(reviewStart, maxReviewStart)
    }, [reviewStart, maxReviewStart])

    const errorByIndex = useMemo(() => {
        const mapped = new Map<number, string>()

        for (const error of props.state.errors) {
            mapped.set(error.index, error.error)
        }

        return mapped
    }, [props.state.errors])

    const validItemsToSend = useMemo(() => {
        return props.state.items.filter((_, i) => {
            return !errorByIndex.has(i)
        })
    }, [props.state.items, errorByIndex])

    const pageItems = useMemo(
        () =>
            props.state.items.slice(
                reviewClampedStart,
                reviewClampedStart + reviewCount,
            ),
        [props.state.items, reviewClampedStart, reviewCount],
    )

    useEffect(() => {
        BrowserStorageHelper.set(
            PAYER_MAPPINGS_CONFIG.reviewListPerPageStorageKey,
            selectedPaginationOption,
            BrowserStorageType.sessionStorage,
        )
    }, [selectedPaginationOption])

    useEffect(() => {
        setReviewStart(0)
    }, [selectedPaginationOption])

    useEffect(() => {
        if (reviewStart > maxReviewStart) {
            setReviewStart(maxReviewStart)
        }
    }, [maxReviewStart, reviewStart])

    const reviewNextPagination = useMemo<IPagination>(() => {
        const start = reviewClampedStart + reviewCount

        return {
            start,
            count: reviewCount,
            total: reviewTotal,
            moreAvailable: start < reviewTotal,
        }
    }, [reviewClampedStart, reviewCount, reviewTotal])

    const onReviewGetNewItems = (pagination: IPagination) => {
        setReviewStart(pagination.start)
    }

    const onReuploadCsv = () => {
        setIsReuploadPopupActive(true)
    }

    const onReuploadDone = (data: IReviewPayerMappingsState) => {
        setIsReuploadPopupActive(false)
        setReviewStart(0)

        props.onReuploadDone(data)
    }

    const upsertValidItems = (bulkMode: PracticePayerMappingsBulkMode) => {
        if (!selectedPracticeRole || !validItemsToSend.length) {
            return
        }

        dispatch(
            payerMappingsUpsertBulk({
                practice: selectedPracticeRole.practice,
                payload: {
                    mode: bulkMode,
                    items: PayerMappingsCsvHelper.toRequestItems(
                        validItemsToSend,
                    ),
                },
                onSuccess: () => {
                    ToastrHelper.success(
                        bulkMode === PracticePayerMappingsBulkMode.REPLACE
                            ? "Payer mappings were replaced"
                            : "Payer mappings were added",
                    )

                    props.onUpsertSuccess()
                },
            }),
        )
    }

    const getUpsertModalContentCopy = (
        bulkMode: PracticePayerMappingsBulkMode,
    ) => {
        const errorCount = props.state.errors.length
        const isErrorsPlural = errorCount > 1

        const updatedCount = props.state.updatedCount
        const isUpdatedCountPlural = updatedCount > 1

        const createdCount = props.state.createdCount
        const isCreatedCountPlural = createdCount > 1

        // Validation ran over every parsed row, so its own deleted count spares the
        // mappings behind the invalid ones. Those rows are dropped from the upsert,
        // so under REPLACE everything not being updated goes - errors included
        const deletedCount = Math.max(
            0,
            payerMappingsState.unfilteredItemsTotal - updatedCount,
        )
        const isDeletedCountPlural = deletedCount > 1

        return (
            <>
                {/* A caveat about the upload, not an outcome of it, so it sits
                    above the list rather than reading as a fourth bullet */}
                {errorCount > 0 && (
                    <PrimaryText
                        className="text-left mb-[12px]"
                        weight="medium"
                    >
                        {`${errorCount} invalid mapping${isErrorsPlural ? "s" : ""} found and won't be processed. Fix and re-upload to include ${isErrorsPlural ? "them" : "it"}.`}
                    </PrimaryText>
                )}

                <ul className="pl-[20px]">
                    <li className="text-left list-disc marker:text-lg">
                        <PrimaryText>
                            {createdCount
                                ? `${createdCount} new payer mapping${isCreatedCountPlural ? "s" : ""} will be added.`
                                : "No new payer mappings will be added."}
                        </PrimaryText>
                    </li>

                    <li className="text-left list-disc marker:text-lg">
                        <PrimaryText>
                            {updatedCount
                                ? `${updatedCount} existing payer mapping${isUpdatedCountPlural ? "s" : ""} will be updated.`
                                : "No existing payer mappings will be updated."}
                        </PrimaryText>
                    </li>

                    <li className="text-left list-disc marker:text-lg">
                        <PrimaryText>
                            {deletedCount > 0 &&
                            bulkMode === PracticePayerMappingsBulkMode.REPLACE
                                ? `${deletedCount} existing mapping${isDeletedCountPlural ? "s" : ""} will be permanently removed${errorCount > 0 ? ", including any matched by an invalid row" : ""}.`
                                : "No existing mappings will be removed."}
                        </PrimaryText>
                    </li>
                </ul>
            </>
        )
    }

    const onAddValidMappings = () => {
        const validItemCount = validItemsToSend.length
        const isValidItemsPlural = validItemCount > 1

        const bulkMode = PracticePayerMappingsBulkMode.ADDITIVE

        ConfirmationHelper.enableConfirmation(
            () => {
                upsertValidItems(bulkMode)
            },
            `Add ${validItemCount} payer mapping${isValidItemsPlural ? "s" : ""} from this CSV?`,
            getUpsertModalContentCopy(bulkMode),
            undefined,
            undefined,
            undefined,
            true,
            {
                isLeftAligned: false,
                width: 600,
            },
        )
    }

    const onReplaceValidMappings = () => {
        const payerMappingCount = payerMappingsState.unfilteredItemsTotal
        const isPayerMappingCountPlural = payerMappingCount > 1

        const validItemCount = validItemsToSend.length

        const bulkMode = PracticePayerMappingsBulkMode.REPLACE

        ConfirmationHelper.enableConfirmation(
            () => {
                upsertValidItems(bulkMode)
            },
            `Replace ${payerMappingCount} existing payer mapping${isPayerMappingCountPlural ? "s" : ""} with ${validItemCount} from this CSV?`,
            getUpsertModalContentCopy(bulkMode),
            undefined,
            undefined,
            undefined,
            true,
            {
                isLeftAligned: false,
                width: 600,
            },
        )
    }

    return (
        <div className="pb-32 relative">
            <NirvanaBreathingLoaderElement isActive={isSubmitting} />

            <div className="relative py-[12px] mb-[24px]">
                <div className="flex items-center md:block">
                    <PrimaryText
                        className="mr-[16px]"
                        size={29}
                        lineHeight={35}
                        weight="semibold"
                    >
                        Review Payer Mappings
                    </PrimaryText>

                    <span
                        className="
                            flex items-center px-[8px] py-[2px]
                            rounded-[4px] bg-white
                        "
                    >
                        <PrimaryText typography="h6" className="mr-[4px]">
                            {props.state.filename}
                        </PrimaryText>

                        <div
                            onClick={props.onClose}
                            className="p-[4px] cursor-pointer"
                        >
                            <img src={closeDarkImg} alt="Close" />
                        </div>
                    </span>
                </div>
            </div>

            <div className="relative pb-[120px]">
                <PayerMappingsTableHeaderComponent />

                {pageItems.length === 0 ? (
                    <PrimaryText className="mt-[24px] text-center opacity-50">
                        No payer mappings found
                    </PrimaryText>
                ) : (
                    pageItems.map((item, index) => {
                        const itemIndex = reviewClampedStart + index

                        return (
                            <PayerMappingsTableRowComponent
                                key={itemIndex}
                                externalName={item.externalName}
                                externalPayerId={item.externalPayerId}
                                payerId={item.payerId}
                                error={errorByIndex.get(itemIndex)}
                            />
                        )
                    })
                )}

                {reviewTotal > 0 ? (
                    <PoliciesListPaginationComponent
                        alignLeft={true}
                        getNewItems={onReviewGetNewItems}
                        nextPagination={reviewNextPagination}
                        onPaginationChange={undefined}
                        selectedPaginationOption={selectedPaginationOption}
                        setSelectedPaginationOption={
                            setSelectedPaginationOption
                        }
                    />
                ) : null}
            </div>

            <PayerMappingsReviewFloatingControlComponent
                invalidCount={props.state.errors.length}
                totalCount={
                    props.state.totalRowCount ?? props.state.items.length
                }
                validCount={validItemsToSend.length}
                onReuploadCsv={onReuploadCsv}
                onAddValidMappings={onAddValidMappings}
                onReplaceValidMappings={onReplaceValidMappings}
                isSubmitting={isSubmitting}
            />

            <PayerMappingsBulkUploadPopupComponent
                isActive={isReuploadPopupActive}
                onDone={() => {
                    setIsReuploadPopupActive(false)
                }}
                onValidationDone={onReuploadDone}
            />
        </div>
    )
}

interface IPayerMappingsReviewComponentProps {
    state: IReviewPayerMappingsState
    onClose: () => void
    onReuploadDone: (data: IReviewPayerMappingsState) => void
    onUpsertSuccess: () => void
}
